import React, { useState } from "react";
import { Box, Typography, Button, Drawer, Chip, IconButton } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import MultiSelectDropdown from "../../../library/ui/MultiSelectDropdown/MultiSelectDropdown";
import { isTablet, isDesktop } from "react-device-detect";

export interface FilterConfig {
  label: string;
  placeholder: string;
  data: Array<{ value: string | number[]; label: string }>;
}

interface ShiftFilterSelectorProps {
  filters: FilterConfig[];
  selectedValues: Array<(string | number[])[]>;
  onFilterChange: (filterIndex: number, values: (string | number[])[]) => void;
  isLoading: boolean;
}

export const ShiftFilterSelector: React.FC<ShiftFilterSelectorProps> = ({
  filters,
  selectedValues,
  onFilterChange,
  isLoading,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [tempSelectedValues, setTempSelectedValues] = useState<Array<(string | number[])[]>>([]);

  const handleOpenDrawer = () => {
    setTempSelectedValues(selectedValues.map(vals => [...vals]));
    setIsDrawerOpen(true);
  };

  const handleTempFilterChange = (filterIndex: number, values: (string | number[])[]) => {
    const newTempValues = [...tempSelectedValues];
    newTempValues[filterIndex] = values;
    setTempSelectedValues(newTempValues);
  };

  const handleApplyFilters = () => {
    tempSelectedValues.forEach((values, index) => {
      onFilterChange(index, values);
    });
    setIsDrawerOpen(false);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTempSelectedValues([]);
  };

  const hasAnySelection = () => {
    return tempSelectedValues.some(values => values && values.length > 0);
  };

  const getSelectedChips = () => {
    const chips: Array<{ filterIndex: number; value: string | number[]; label: string }> = [];
    
    filters.forEach((filter, filterIndex) => {
      const selectedItems = selectedValues[filterIndex] || [];
      selectedItems.forEach((selectedValue) => {
        const option = filter.data.find(opt => 
          JSON.stringify(opt.value) === JSON.stringify(selectedValue)
        );
        if (option) {
          chips.push({
            filterIndex,
            value: selectedValue,
            label: option.label,
          });
        }
      });
    });
    
    return chips;
  };

  const handleChipDelete = (filterIndex: number, valueToRemove: string | number[]) => {
    if (isLoading) return;
    const currentValues = selectedValues[filterIndex] || [];
    const newValues = currentValues.filter(
      val => JSON.stringify(val) !== JSON.stringify(valueToRemove)
    );
    onFilterChange(filterIndex, newValues);
  };

  const renderFilters = (useTempState: boolean = false) => (
    <>
      {filters.map((filter, index) => {
        const valuesToUse = useTempState ? (tempSelectedValues[index] || []) : (selectedValues[index] || []);
        const onChangeHandler = useTempState ? handleTempFilterChange : onFilterChange;
        
        return (
          <Box 
            key={index} 
            sx={{ 
              flex: !isTablet && !isDesktop ? "1 1 100%" : { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)", md: "1 1 calc(50% - 12px)" },
              mb: !isTablet && !isDesktop ? 2 : 0,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                mb: 1.5,
                color: "#000000",
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: 500,
              }}
            >
              {filter.label}
            </Typography>
            <MultiSelectDropdown
              options={filter.data}
              selectedValues={valuesToUse}
              onChange={(values) => onChangeHandler(index, values)}
              placeholder={filter.placeholder}
              disabled={isLoading}
            />
          </Box>
        );
      })}
    </>
  );

  const selectedChips = getSelectedChips();

  return (
    <>
      {/* Mobile: Filter Button */}
      {!isTablet && !isDesktop ? (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={handleOpenDrawer}
            disabled={isLoading}
            sx={{
              backgroundColor: "#10389A",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#0c2d7a",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E0E0E0",
                color: "#9E9E9E",
              },
            }}
          >
            Filter
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            mb: { xs: 3, md: 4 },
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 2, md: 3 },
          }}
        >
          {renderFilters(false)}
        </Box>
      )}

      {!isTablet && !isDesktop && selectedChips.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 3,
          }}
        >
          {selectedChips.map((chip, index) => (
            <Chip
              key={index}
              label={chip.label}
              onDelete={() => handleChipDelete(chip.filterIndex, chip.value)}
              deleteIcon={<CancelIcon />}
              disabled={isLoading}
              sx={{
                backgroundColor: "#E3F2FD",
                color: "#000000",
                fontWeight: 500,
                fontSize: "14px",
                "& .MuiChip-deleteIcon": {
                  color: "#6666666e",
                  "&:hover": {
                    color: "#6666666e",
                  },
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                },
              }}
            />
          ))}
        </Box>
      )}

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            width: "85%",
            maxWidth: "400px",
            p: 3,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "20px",
              color: "#000000",
            }}
          >
            Filter
          </Typography>
          <IconButton
            onClick={handleCloseDrawer}
            sx={{ color: "#666666" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography
          variant="body2"
          sx={{
            mb: 3,
            color: "#666666",
            fontSize: "14px",
          }}
        >
          To refine search, apply filters as needed.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {renderFilters(true)}
        </Box>

        <Box sx={{ mt: "auto", pt: 4 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApplyFilters}
            disabled={!hasAnySelection()}
            sx={{
              backgroundColor: "#10389A",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "16px",
              py: 1.5,
              "&:hover": {
                backgroundColor: "#0c2d7a",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E0E0E0",
                color: "#9E9E9E",
              },
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

